import { Feature, LineString, Polygon, Position } from "geojson";
import turf from "../../../../shared/utils/turf";
import { importHlaUtils } from "../utils";
import { RootStore } from "../../../../shared/stores/RootStore";
import { toast, toaster } from "../../../../shared/views/CustomToaster";
import { HlaWayType } from "../../../../hla/models/HlaWay";
import { createWayFromLineFeature } from "../createWayFromLineFeature";
import { MapMode } from "../../../../survey/stores/MapStore";
import { roadAndPathLayers } from "../../../utils/mapbox";
import { calculateGeographicHeading } from "../../../utils/map-utils";

export async function importHlaFromAToB(
  route: Position[],
  rootStore: RootStore,
  map: mapboxgl.Map,
  options: {
    frontSide: "left" | "right";
    backSide: "left" | "right";
    rightHandTraffic: boolean;
  },
) {
  if (route.length < 2) return;

  const routeLine = turf.lineString(route);

  importHlaUtils.centerMapAround(routeLine, map);

  const featureRefs = new Set<string>();
  const features = route.flatMap((position, index) => {
    if (index === route.length - 1) return [];

    const nextPosition = route[index + 1];
    const segment = [position, nextPosition];
    const lineString = turf.lineString(segment);

    const features = queryRoadsAndPathsAlongLine(lineString, map).filter(
      // remove duplicated
      (f) => !featureRefs.has(JSON.stringify(f.geometry)),
    );

    features.forEach((f) => featureRefs.add(JSON.stringify(f.geometry)));

    return features;
  });

  const { mapQueryStore, hlaStore, surveyStore } = rootStore;
  const totalFeatures = features.length;
  let featureCounter = 0;
  let wayCounter = 0;

  const toastId = toast("loading", `Querying route selection. Please wait...`);

  const survey = surveyStore.getSelected()!;
  const enabledCategories = Object.values(HlaWayType);

  for (const feature of features) {
    const featureId = feature.id as number;

    const way = await createWayFromLineFeature(
      featureId,
      feature.geometry.coordinates,
      feature.properties,
      enabledCategories,
      map,
      options,
    );
    if (way) {
      way.surveyId = survey.id;
      await hlaStore.create(way);
      // this.addRequestToMap(way);
      wayCounter++;
    }

    featureCounter++;
    toaster.update(toastId, {
      title: `Processing features (${featureCounter} of ${totalFeatures})`,
    });
  }

  toaster.update(toastId, {
    type: "success",
    title: `Done! ${wayCounter} ways imported`,
  });

  hlaStore.updateMapSource();
  mapQueryStore.deactivate();

  const { mapStore } = rootStore;
  mapStore.setMode(MapMode.SurveyPage);
}

function queryRoadsAndPathsAlongLine(
  routeLine: Feature<LineString>,
  map: mapboxgl.Map,
) {
  // search for features near the center of the segment
  const trimmedLine = trimLine(routeLine);
  const bufferPolygon = turf.buffer(trimmedLine, 2, {
    units: "meters",
  }) as Feature<Polygon>;

  const heading = calculateGeographicHeading(
    trimmedLine.geometry.coordinates[0],
    trimmedLine.geometry.coordinates[1],
  );

  const bbox = importHlaUtils.polygonToBbox(bufferPolygon, map);
  return importHlaUtils
    .flattenLineAndMultiLineFeatures(
      map.queryRenderedFeatures(bbox, {
        layers: roadAndPathLayers,
      }),
    )
    .filter((feature) => turf.booleanIntersects(feature, bufferPolygon))
    .filter((feature) => {
      // make sure the heading of the line is similar to the route we're querying
      const segments = importHlaUtils.intersectLineAndPolygon(
        feature,
        bufferPolygon,
      );

      if (!segments.length) return false;

      return segments.some((segment) => {
        const line = segment.geometry.coordinates;
        const intersectionHeading = calculateGeographicHeading(
          line[0],
          line[1],
        );

        return importHlaUtils.anglesEqualDirectionless(
          heading,
          intersectionHeading,
          15,
        );
      });
    });
}

function trimLine(
  lineFeature: Feature<LineString>,
  trimMeters = 3,
): Feature<LineString> {
  const totalLength = turf.length(lineFeature, { units: "meters" });

  // Ensure the line is long enough to trim
  if (totalLength <= trimMeters * 2) {
    return lineFeature;
  }

  // Slice from trimMeters to (totalLength - trimMeters)
  const trimmed = turf.lineSliceAlong(
    lineFeature,
    trimMeters,
    totalLength - trimMeters,
    { units: "meters" },
  );

  return trimmed;
}
